import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { logout } from "../services/api";
import { useCatalog } from "../state/CatalogContext";
import * as I from "./Icons";

const NAV = [
  { to: "/", label: "Inicio", end: true },
  { to: "/peliculas", label: "Películas" },
  { to: "/series", label: "Series" },
  { to: "/anime", label: "Anime" },
  { to: "/canales", label: "Canales" },
];

export function Logo({ className = "" }: { className?: string }) {
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <img src="/favicon.svg" alt="" width={28} height={28} className="rounded-md" />
      <span className="font-display text-xl font-semibold tracking-tight">Radiant</span>
    </span>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const [scrolled, setScrolled] = useState(false);
  const { session } = useCatalog();
  const nav = useNavigate();
  const loc = useLocation();
  useEffect(() => {
    const on = () => setScrolled(scrollY > 24);
    on();
    addEventListener("scroll", on, { passive: true });
    return () => removeEventListener("scroll", on);
  }, []);
  useEffect(() => {
    scrollTo(0, 0);
  }, [loc.pathname]);

  return (
    <div className="min-h-screen pb-20 md:pb-0">
      <a href="#contenido" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 btn">Saltar al contenido</a>
      <header className={`fixed inset-x-0 top-0 z-40 transition-colors ${scrolled ? "bg-ink/95 backdrop-blur border-b border-line/60" : "bg-gradient-to-b from-ink/80 to-transparent"}`}>
        <div className="mx-auto flex h-16 max-w-[1800px] items-center gap-8 px-4 sm:px-8 lg:px-12">
          <Link to="/" aria-label="Radiant TV, inicio"><Logo /></Link>
          <nav className="hidden gap-6 md:flex" aria-label="Principal">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `text-[15px] transition-colors ${isActive ? "text-text" : "text-muted hover:text-text"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-1">
            <Link to="/buscar" className="icon-btn" aria-label="Buscar"><I.Search /></Link>
            {session && !session.public && (
              <button className="btn btn-ghost hidden text-sm sm:inline-flex" onClick={async () => { await logout(); nav(0); }}>Cerrar sesión</button>
            )}
          </div>
        </div>
      </header>
      <main id="contenido">{children}</main>
      <nav className="fixed inset-x-0 bottom-0 z-40 flex border-t border-line bg-ink/95 backdrop-blur md:hidden" aria-label="Principal (móvil)">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `flex-1 py-3 text-center text-xs ${isActive ? "text-accent" : "text-muted"}`}>
            {n.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

export function Page({ children, title }: { children: ReactNode; title?: string }) {
  useEffect(() => {
    document.title = title ? `${title} — Radiant TV` : "Radiant TV";
  }, [title]);
  return <div className="mx-auto max-w-[1800px] px-4 pt-24 sm:px-8 lg:px-12">{children}</div>;
}
